import isEqual from 'lodash.isequal'
import { IAnalyticsTagManager } from '../../domain/analytics/analytics-configuration'
import { IScrollData } from '../../domain/analytics/providers/scroll-depth'
import { IPageConfiguration } from '../../domain/conf/page'
import { IEvent } from '../../domain/event/event'
import { logger, loggerTimeLog, TimerLabels } from '../../utils/common/logger'
import { DOMUtils } from '../../utils/dom'
import { SimpleEvents } from '../../utils/events'
import { IProfile } from '../common/profile'
import { IProviderConfig } from '../common/provider-config'
import { IAnalyticsAdapter } from './adapters/adapter'
import { ITagManager } from './tags'

export interface IAnalyticsServiceConfiguration {
    // ad
    adSite?: string
    // analytics
    provider: IProviderConfig
    brand?: string
    domain?: string
    division?: string
    debug?: string
    // identity
    profile: IProfile
    // page
    page: IPageConfiguration
    tags?: IAnalyticsTagManager
    platform: string
    platformVersion: string
    femVersion: string
    scrollData?: IScrollData
    isDev?: boolean
}

export class AnalyticsService extends SimpleEvents {
    public static ACTION_PAGE_LOAD = 'page-load'

    public static DOM = {
        attrs: {
            action: 'data-evt',
            async: 'data-async',
            data: 'data-evt-val',
            multiAsync: 'data-multi-async',
            type: 'data-evt-typ'
        }
    }

    public static scrollEventTypes = {
        pageScroll: 'page_scroll', // deprecates old `scroll-depth` event type
        percentPageview: 'percent-pageview'
    }

    public static customEventNames = {
        registration: 'registration',
        signin: 'signin',
        signout: 'signout',
        submissionError: 'submission_error',
        userAlias: 'user_alias'
    }

    public static focusEventTypes = {
        formFirstInteraction: 'form_interaction',
    }

    public conf: IAnalyticsServiceConfiguration
    public meta: object

    public constructor(
        public adapter: IAnalyticsAdapter<IProviderConfig>,
        public tagManager: ITagManager) {
        super()
    }

    public async initialize(conf: IAnalyticsServiceConfiguration): Promise<void> {
        logger('debug', `${TimerLabels.PAGE_LOAD} - Analytics service initializing`)
        loggerTimeLog(TimerLabels.PAGE_LOAD)
        if ( !this.conf || !isEqual(this.conf, conf)) {
            this.conf = conf
            // format meta
            this.meta = {...this.conf}

            if (conf.tags) {
                this.tagManager.init(conf.tags, this.conf)
            }

            // initialize adapter
            this.adapter.initialize(conf.provider, conf.profile, await this.getDocEvents(), this.meta, this.conf,
                this.conf.isDev)

            // initialize mutation observers to trace dynamic contents
            await this.assignMutationObservers(AnalyticsService.DOM.attrs.async)
            await this.assignMutationObservers(AnalyticsService.DOM.attrs.multiAsync, true)
            logger('debug', `${TimerLabels.PAGE_LOAD} - Analytics service initialized`)
            loggerTimeLog(TimerLabels.PAGE_LOAD)
        }

    }

    public update(profile: IProfile): void {
        // update meta
        this.meta = {
            ...this.conf,
            profile
        }

        this.adapter.update(profile)
    }

    private async getDocEvents(dom?: Element): Promise<IEvent[]> {
        await DOMUtils.ready

        const target = (dom || document).querySelectorAll(`[${AnalyticsService.DOM.attrs.action}]`)

        return DOMUtils.nodes(target)
            .map((element: Element) => this.adapter.getEventByType(
                element.getAttribute(AnalyticsService.DOM.attrs.type) || 'event',
                element.getAttribute(AnalyticsService.DOM.attrs.data) || '',
                element.getAttribute(AnalyticsService.DOM.attrs.action) || 'click',
                element))
    }

    /**
     * Assign MutationObserver into DOM which has data-async or data-multi-async attribute
     * When isMultiMutation is true, do not remove the observer as there could be more mutations
     * Use case: in vehicle finder, the user can load more cards multiple times
     */
    private async assignMutationObservers(asyncAttribute: string,
                                          isMultiMutation?: boolean): Promise<MutationObserver[]> {
        await DOMUtils.ready

        return DOMUtils.nodes(document.querySelectorAll(`[${asyncAttribute}]`))
            .map((element: Element) => {
                const mutationObserver = new MutationObserver(async (mutations, observer) => {
                    // Multipe mutations may happen for same target element
                    // Therefore, make sure that unique target elements are used for attaching event handlers
                    // (eg: articles are lazy loaded and also native ad stories are added inside the same child list)
                    const mutatedChildElementTargets = [...new Set(mutations.map((item) => item.target))]
                    // Find Events from a new DOM and bind events
                    mutatedChildElementTargets.forEach((childElement) => {
                        this.getDocEvents(childElement as Element)
                            .then((events: IEvent[]) => {
                                if (0 === events.length) {
                                    return
                                }
                                this.adapter.bindEvents(events, this.meta)
                                if (!isMultiMutation) {
                                    observer.disconnect()
                                }
                            })
                            .catch(console.error)
                    })
                })
                mutationObserver.observe(element, { childList: true, subtree: true })
                return mutationObserver
            })
    }
}
